import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {Platform, StatusBar, useColorScheme} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {darkTheme, lightTheme} from '../config/themes';

export const ThemeContext = createContext();

/**
 * Enhanced ThemeProvider with system-level integration
 * - Respects system dark/light mode by default
 * - Allows user override
 * - Automatically updates on system appearance change (Android 10+)
 */
export const ThemeProvider = ({ children }) => {
    const systemScheme = useColorScheme(); // System dark/light mode
    const [themeMode, setThemeModeState] = useState('system'); // 'system', 'light', 'dark'
    const [theme, setTheme] = useState(lightTheme);
    const [isInitialized, setIsInitialized] = useState(false);

    // Load saved theme preference on mount
    useEffect(() => {
        const loadThemeMode = async () => {
            try {
                const savedMode = await AsyncStorage.getItem('userThemeMode');
                if (savedMode) {
                    setThemeModeState(savedMode);
                }
                setIsInitialized(true);
            } catch (error) {
                console.warn('[ThemeContext] Failed to load theme mode:', error);
                setIsInitialized(true);
            }
        };
        loadThemeMode();
    }, []);

    // Update theme when mode or system scheme changes
    useEffect(() => {
        if (!isInitialized) return;

        const updateTheme = (mode) => {
            if (mode === 'system') {
                // Default to system setting
                const currentScheme = systemScheme || 'light';
                setTheme(currentScheme === 'dark' ? darkTheme : lightTheme);
                
                // Update status bar style for better appearance integration
                if (Platform.OS === 'android') {
                    StatusBar.setBarStyle(currentScheme === 'dark' ? 'light-content' : 'dark-content', true);
                }
            } else if (mode === 'dark') {
                setTheme(darkTheme);
                if (Platform.OS === 'android') {
                    StatusBar.setBarStyle('light-content', true);
                }
            } else {
                setTheme(lightTheme);
                if (Platform.OS === 'android') {
                    StatusBar.setBarStyle('dark-content', true);
                }
            }
        };

        updateTheme(themeMode);
    }, [themeMode, systemScheme, isInitialized]);

    // Memoized setter to persist theme mode
    const setThemeModeWrapper = useCallback(async (mode) => {
        setThemeModeState(mode);
        try {
            await AsyncStorage.setItem('userThemeMode', mode);
        } catch (error) {
            console.warn('[ThemeContext] Failed to save theme mode:', error);
        }
    }, []);

    // Toggle between light and dark
    const toggleTheme = useCallback(() => {
        setThemeModeWrapper(themeMode === 'dark' ? 'light' : 'dark');
    }, [themeMode, setThemeModeWrapper]);

    // Get contrast-safe colors for high-visibility (important for POS)
    const getHighContrastColors = useCallback(() => {
        const isDark = themeMode === 'dark' || (themeMode === 'system' && systemScheme === 'dark');
        return {
            primary: isDark ? '#ffeb3b' : '#ff6f00',
            background: isDark ? '#000000' : '#ffffff',
            text: isDark ? '#ffffff' : '#000000',
            success: '#4caf50',
            warning: '#ff9800',
            error: '#f44336',
        };
    }, [themeMode, systemScheme]);

    const value = useMemo(
        () => ({
            theme,
            themeMode,
            setThemeMode: setThemeModeWrapper,
            toggleTheme,
            getHighContrastColors,
            isDark: themeMode === 'dark' || (themeMode === 'system' && systemScheme === 'dark'),
            isInitialized,
        }),
        [theme, themeMode, setThemeModeWrapper, toggleTheme, getHighContrastColors, systemScheme, isInitialized]
    );

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

/**
 * Hook to use theme in components
 */
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider');
    }
    return context;
};
